"use client";

/**
 * The top bar, which names where you are and carries the program.
 *
 * Extracted from the prototype's `OneApp` rather than rewritten — it was
 * written inline there, so promoting it meant lifting the markup out whole and
 * giving the two hosts the same component instead of two headers that look
 * alike until one of them changes.
 *
 * On a phone it carries the product's name, on every screen, and that is the
 * one place the product is named at all: there is no rail below this width, so
 * a header that swapped the wordmark for the screen's title left the app
 * anonymous everywhere except Home. It is also the way back. A phone app names
 * itself in the corner and that corner goes home, which is the affordance a
 * tab bar alone does not give you from a screen that is not a tab.
 *
 * Past rail width the wordmark is already two rows up in the rail, so the
 * header names the screen instead of saying the same thing twice.
 *
 * The second is the program, and that one is not cosmetic. On a phone the rail
 * does not exist, so this is the only place a creator on more than one program
 * can switch between them. A header that only appears at desk width is a
 * feature that only exists at desk width.
 *
 * Chrome reaches the edges, content does not: the bar spans the pane while its
 * contents sit on the same column as the screen below, so the title shares a
 * left edge with the first card under it.
 */

import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";
import { CONTROL_PRESS } from "@/components/ui/design-system/surface-styles";
import { Rail, Title } from "@/components/ui/design-system/text";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { CHROME, HEADER_H, HEADER_WIDE_H } from "./chrome";
import type { Tab } from "./tabs";
import { NAV_LABELS } from "./tabs";

export function TopBar({
  tab,
  wide,
  topInset,
  columnClassName,
  trailing,
  leading,
  homeHref,
}: {
  tab: Tab;
  /** Past `WIDE` the rail names the product, so the header names the screen. */
  wide: boolean;
  /**
   * What the device owes the top edge, as a CSS length.
   *
   * The prototype's simulated frame publishes a status-bar height; a real
   * browser is asked for `env(safe-area-inset-top)`. Either way the bar grows
   * by it rather than letting the notch sit on the title.
   */
  topInset: string;
  /** The host's content column, so the title lines up with the cards below. */
  columnClassName: string;
  /** The program control, supplied by the host as the rail's `switcher` is. */
  trailing?: ReactNode;
  /**
   * Replaces the title outright: an open thread puts a back affordance and
   * the correspondent here instead of the screen's name.
   */
  leading?: ReactNode;
  /**
   * Where the wordmark goes. Null on Home, where it is already there and a
   * link to the screen you are on is a control that does nothing.
   */
  homeHref?: string | null;
}) {
  const t = useCreatorTranslations("shell");
  return (
    <header
      style={
        {
          paddingTop: wide ? 0 : topInset,
          height: wide
            ? `${HEADER_WIDE_H}px`
            : `calc(${HEADER_H}px + ${topInset})`,
        } as CSSProperties
      }
      className={cn("absolute inset-x-0 top-0 z-20 flex items-center", CHROME)}
    >
      <div
        className={cn(
          "mx-auto flex w-full items-center gap-nf-ds-item",
          columnClassName,
        )}
      >
        {leading ??
          (wide ? (
            <Title className="min-w-0 truncate text-[17px] lg:text-[15px]">
              {t(NAV_LABELS[tab])}
            </Title>
          ) : (
            <Wordmark href={tab === "home" ? null : homeHref} />
          ))}

        {/* The program is identity, so on a phone it stays in the header — but
            as a real target with the name legible, not a truncated trigger
            wedged against a divider. */}
        {wide || !trailing ? null : (
          <div className="ml-auto flex min-w-0 shrink items-center">
            {trailing}
          </div>
        )}
      </div>
    </header>
  );
}

function Wordmark({ href }: { href?: string | null }) {
  const t = useCreatorTranslations("shell");
  const mark = (
    <>
      <span className="font-swear text-[22px] leading-none tracking-[-0.01em]">
        NewForm
      </span>
      <Rail className="text-nf-ds-brand">One</Rail>
    </>
  );

  const shape = "flex min-w-0 items-baseline gap-nf-ds-2";

  if (!href) return <span className={shape}>{mark}</span>;

  return (
    <Link
      href={href}
      aria-label={t("tabs.home.nav")}
      className={cn(
        shape,
        "rounded-[8px] outline-hidden",
        "focus-visible:ring-2 focus-visible:ring-nf-ds-brand focus-visible:ring-offset-2",
        CONTROL_PRESS,
      )}
    >
      {mark}
    </Link>
  );
}
